import { useEffect, useRef, useState, useCallback } from 'react';

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

/**
 * Lightweight parallax hook using Intersection Observer + RAF
 * Only runs parallax calculation when element is visible in viewport
 *
 * @param {number} strength - Parallax strength (0.1 = subtle, 1.0 = strong)
 * @returns {Object} { elementRef, offset } - Ref to attach to element and calculated offset
 */
export const useParallax = (strength = 0.3) => {
  const elementRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const isVisibleRef = useRef(false);
  const rafRef = useRef(null);

  // Memoized scroll handler to avoid recreating on every render
  const handleScroll = useCallback(() => {
    if (!isBrowser) return;

    const element = elementRef.current;
    if (!element || !isVisibleRef.current) return;

    // Cancel previous frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    // Use RAF for smooth 60fps animation
    rafRef.current = requestAnimationFrame(() => {
      const rect = element.getBoundingClientRect();
      const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      const parallaxOffset = scrollProgress * strength * 100 - 25;

      setOffset(parallaxOffset);
    });
  }, [strength]);

  useEffect(() => {
    if (!isBrowser) return;

    const element = elementRef.current;
    if (!element) return;

    // Throttled scroll handler
    let ticking = false;
    const optimizedScroll = () => {
      if (!ticking && isVisibleRef.current) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Only observe scroll when element is near viewport
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          // Calculate immediately when becoming visible
          handleScroll();
        }
      },
      {
        rootMargin: '200px', // Start tracking 200px before entering viewport
      }
    );

    observer.observe(element);
    window.addEventListener('scroll', optimizedScroll, { passive: true });

    // Initial calculation
    handleScroll();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', optimizedScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleScroll]);

  return { elementRef, offset };
};
